import React, { useState, useEffect } from 'react';
import { PhoneCall, Mail, MapPin, CheckCircle2, Star, Menu, X, ChevronDown, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { siteData } from '../data/siteData';
import QuoteModal from '../components/QuoteModalNew';
import { Link, useLocation } from 'react-router-dom';
import { NavLinks, navLinks, setSeo, HeaderWhatsApp, MobileWhatsApp } from '../components/PageLayout';

// Soft pink palette
const C = { accent: '#FD3DB5', accentLight: '#ff6bc9', bg: '#0b0b0f', card: '#121218', muted: '#1a1a22' };

const HOME_TITLE = 'Hire Topless Waitresses Melbourne | #1 Topless Waitressing Agency';
const HOME_DESC = 'Hire topless waitresses in Melbourne from the highest-rated topless waitressing agency. Bucks night entertainment, strip shows, poker dealers. 5-star reviews. Book your topless waitress today.';

const HomePage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showFloat, setShowFloat] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);

  const { hash } = useLocation();

  useEffect(() => {
    setSeo({ title: HOME_TITLE, description: HOME_DESC, path: '/' });
  }, []);

  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' }), 100);
    return () => clearTimeout(t);
  }, [hash]);

  useEffect(() => {
    const h = () => setShowFloat(window.scrollY > 600);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  const d = siteData;

  const GoogleG = () => (
    <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );

  const serif = { fontFamily: "'Cormorant Garamond', Georgia, serif" };

  return (
    <div className="min-h-screen" style={{ background: C.bg, fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="homepage">

      {/* ── Header ── */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b" style={{ background: `${C.bg}ee`, borderColor: `${C.accent}15` }} data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-[72px]">
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <span className="text-2xl font-bold" style={{ ...serif, color: C.accent }}>TW</span>
            <div className="hidden sm:block lg:hidden xl:block">
              <h1 className="text-sm font-semibold text-white leading-tight tracking-wide">{d.business.name}</h1>
              <p className="text-[10px] tracking-[0.2em] uppercase" style={{ color: `${C.accent}99` }}>{d.business.tagline}</p>
            </div>
          </Link>
          <nav className="hidden lg:flex items-center gap-5 xl:gap-7" data-testid="desktop-nav">
            <NavLinks testPrefix="nav" className="text-[11px] tracking-[0.12em] uppercase font-medium" />
            <HeaderWhatsApp />
            <Button className="text-[12px] font-semibold px-6 h-10 tracking-wide text-white" style={{ background: C.accent }} onClick={() => setIsModalOpen(true)} data-testid="header-cta">
              <PhoneCall className="w-4 h-4 mr-2" />Book Now
            </Button>
          </nav>
          <button className="lg:hidden text-white p-2" data-testid="mobile-menu-toggle" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileMenu && (
          <div className="lg:hidden px-4 py-4 space-y-3" style={{ background: C.card, borderTop: `1px solid ${C.accent}15` }}>
            <NavLinks testPrefix="mobile-nav" className="block py-2 text-sm tracking-wide uppercase" onClick={() => setMobileMenu(false)} />
            <MobileWhatsApp />
            <Button className="w-full text-white font-semibold" style={{ background: C.accent }} onClick={() => { setIsModalOpen(true); setMobileMenu(false); }}>
              <PhoneCall className="w-4 h-4 mr-2" />Book Now
            </Button>
          </div>
        )}
      </header>

      {/* ── Hero ── */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0">
          <video autoPlay loop muted playsInline preload="auto" className="w-full h-full object-cover opacity-50">
            <source src="./videos/hero-bg.webm" type="video/webm" />
            <source src="./videos/hero-bg.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${C.bg}88 0%, ${C.bg}55 50%, ${C.bg}88 100%)` }} />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 w-full pt-28 pb-20 text-center">
          <div className="inline-flex items-center gap-2 mb-8 px-4 py-2 rounded-full" style={{ background: `${C.accent}15`, border: `1px solid ${C.accent}30` }}>
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: C.accent }} />
            <span className="text-xs font-semibold tracking-[0.2em] uppercase" style={{ color: C.accent }}>Melbourne's #1 Rated Agency</span>
          </div>
          <h2 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6 leading-[1.08]" style={serif}>
            #1 Topless Waitressing<br/>& <span style={{ color: C.accent }}>Bucks Nights</span><br/>in Melbourne
          </h2>
          <div className="inline-flex items-center gap-3 mb-8 px-5 py-3 rounded-xl" style={{ background: `linear-gradient(135deg, ${C.accent}20, ${C.accent}08)`, border: `2px solid ${C.accent}50` }} data-testid="hero-badge">
            <Star className="w-6 h-6 fill-amber-400 text-amber-400 flex-shrink-0" />
            <span className="text-white font-bold text-base sm:text-lg">The most reviewed Topless Waitressing Business in Melbourne</span>
          </div>
          <p className="text-lg text-gray-400 mb-10 leading-relaxed max-w-2xl mx-auto">Stunning topless waitresses, incredible shows & complete bucks party packages. Over 1,000 events across Melbourne.</p>
          <div className="flex flex-wrap gap-3 justify-center mb-10">
            {[['10+', 'Years'], ['1000+', 'Events'], ['5.0★', 'Google']].map(([n, l]) => (
              <div key={n} className="px-5 py-3 rounded-xl backdrop-blur-sm" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="text-xl font-bold text-white">{n}</div>
                <div className="text-[10px] text-gray-500 uppercase tracking-[0.15em]">{l}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-center">
            <Button size="lg" className="group relative text-white font-bold h-16 px-12 text-lg rounded-full shadow-2xl hover:scale-105 transition-transform duration-300 overflow-hidden" style={{ background: `linear-gradient(135deg, ${C.accent}, #d42d95)`, boxShadow: `0 12px 40px ${C.accent}50` }} onClick={() => setIsModalOpen(true)} data-testid="hero-cta-button">
              <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
              <PhoneCall className="w-6 h-6 mr-3 relative z-10" /><span className="relative z-10">Book Your Show</span>
            </Button>
          </div>
        </div>
      </section>

      {/* ── Why Choose Us ── */}
      <section className="py-24" style={{ background: C.bg }} data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={serif}>Why Choose Us</h2>
            <div className="w-16 h-0.5 mx-auto" style={{ background: C.accent }} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {d.features.map((f, i) => (
              <div key={i} className="group p-8 rounded-2xl transition-all duration-300 hover:-translate-y-1" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6" style={{ background: `${C.accent}15`, color: C.accent }}>
                  {React.cloneElement(f.icon, { className: "w-7 h-7" })}
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{f.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gallery — 1 photo per girl, 3 per row ── */}
      <section id="gallery" className="py-24" style={{ background: C.bg }} data-testid="gallery-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4" style={serif}>Meet Our Girls</h2>
            <p className="text-gray-500">{d.gallery.length} stunning models available for your event</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.gallery.map((girl, i) => (
              <div
                key={i}
                className="group relative rounded-2xl overflow-hidden cursor-pointer aspect-[3/4]"
                data-testid={`gallery-item-${i}`}
              >
                <img src={girl.photo} alt={girl.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <span className="text-white font-bold text-lg tracking-wide" style={serif}>{girl.name}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About (brief) ── */}
      <section className="py-24 bg-[#f7f7f9]" data-testid="about-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" style={serif}>{d.about.title}</h2>
          <div className="w-16 h-0.5 mx-auto mb-8" style={{ background: C.accent }} />
          <div className="text-gray-600 text-base leading-relaxed space-y-4 mb-12" dangerouslySetInnerHTML={{ __html: d.about.paragraphs.join('') }} />
          <div className="grid sm:grid-cols-3 gap-6 mb-12">
            {d.about.why_choose.map((w, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <div className="text-3xl font-bold mb-2" style={{ ...serif, color: C.accent }}>{w.stat}</div>
                <p className="text-sm text-gray-500">{w.desc}</p>
              </div>
            ))}
          </div>
          <Button size="lg" className="text-white font-semibold px-10 h-13 text-base shadow-lg" style={{ background: C.accent, boxShadow: `0 8px 30px ${C.accent}30` }} onClick={() => setIsModalOpen(true)}>
            <PhoneCall className="w-5 h-5 mr-2" />Book Your Party
          </Button>
        </div>
      </section>

      {/* ── Google Reviews ── */}
      <section id="reviews" className="py-24" style={{ background: C.bg }} data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-3" style={serif}>Google Reviews</h2>
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="flex">{[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-amber-400 text-amber-400" />)}</div>
              <span className="text-gray-300 font-semibold">5.0</span>
            </div>
            <p className="text-gray-500 text-sm">{d.testimonials.length} reviews on Google</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {d.testimonials.map((t, i) => (
              <div key={i} className="rounded-2xl p-6 transition-all duration-200" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }} data-testid={`review-card-${i}`}>
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0" style={{ background: `linear-gradient(135deg, ${C.accent}, ${C.accentLight})` }}>
                    {t.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="font-semibold text-white text-base truncate">{t.name}</p>
                      <GoogleG />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{t.time}</p>
                  </div>
                </div>
                <div className="flex mb-3">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-gray-400 text-sm leading-relaxed">{t.review}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO Content (Part 1) ── */}
      <section className="py-24 bg-white" data-testid="seo-content-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>{d.seoContent.title}</h2>
            <div className="w-16 h-0.5 mx-auto" style={{ background: C.accent }} />
          </div>
          <article className="prose prose-lg max-w-none">
            {d.seoContent.sections.slice(0, 3).map((s, i) => (
              <div key={i} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={serif}>{s.heading}</h2>
                <div className="text-gray-600 text-[15px] leading-[1.85] [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul_li]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol_li]:mb-2 [&_strong]:text-gray-800 [&_table]:w-full [&_table]:mb-4 [&_table_tr]:border-b [&_table_tr]:border-gray-100 [&_table_td]:py-3 [&_table_td]:pr-4 [&_table_td:first-child]:font-semibold [&_table_td:first-child]:text-gray-800 [&_table_td:first-child]:whitespace-nowrap" dangerouslySetInnerHTML={{ __html: s.content }} />
                <div className="w-12 h-px bg-gray-200 mt-10" />
              </div>
            ))}
          </article>
        </div>
      </section>

      {/* ── Services (in middle of content) ── */}
      <section id="services" className="py-24 bg-[#f7f7f9]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>Our Services</h2>
            <p className="text-gray-500 max-w-lg mx-auto">Premium entertainment for every occasion</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {d.services.map((s, i) => (
              <div key={i} className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100" data-testid={`service-card-${i}`}>
                <div className="relative h-60 overflow-hidden">
                  <img src={s.image} alt={s.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                  <h3 className="absolute bottom-4 left-5 text-xl font-bold text-white" style={serif}>{s.title}</h3>
                </div>
                <div className="p-5">
                  <p className="text-gray-600 text-sm mb-4 leading-relaxed">{s.description}</p>
                  <ul className="space-y-2 mb-5">
                    {s.features.map((feat, j) => (
                      <li key={j} className="flex items-center text-sm text-gray-700">
                        <CheckCircle2 className="w-4 h-4 mr-2 flex-shrink-0" style={{ color: C.accent }} />{feat}
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" className="w-full text-sm font-semibold transition-all hover:text-white" style={{ borderColor: `${C.accent}60`, color: C.accent }} onClick={() => setIsModalOpen(true)}
                    onMouseEnter={e => { e.target.style.background = C.accent; e.target.style.color = '#fff'; e.target.style.borderColor = C.accent; }}
                    onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = C.accent; e.target.style.borderColor = `${C.accent}60`; }}>
                    Enquire Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SEO Content (Part 2) ── */}
      <section className="py-24 bg-white" data-testid="seo-content-section-2">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            {d.seoContent.sections.slice(3).map((s, i) => (
              <div key={i} className="mb-12">
                <h2 className="text-2xl font-bold text-gray-900 mb-4" style={serif}>{s.heading}</h2>
                <div className="text-gray-600 text-[15px] leading-[1.85] [&_h3]:text-lg [&_h3]:font-bold [&_h3]:text-gray-800 [&_h3]:mt-6 [&_h3]:mb-3 [&_p]:mb-4 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ul_li]:mb-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_ol_li]:mb-2 [&_strong]:text-gray-800 [&_table]:w-full [&_table]:mb-4 [&_table_tr]:border-b [&_table_tr]:border-gray-100 [&_table_td]:py-3 [&_table_td]:pr-4 [&_table_td:first-child]:font-semibold [&_table_td:first-child]:text-gray-800 [&_table_td:first-child]:whitespace-nowrap" dangerouslySetInnerHTML={{ __html: s.content }} />
                {i < d.seoContent.sections.slice(3).length - 1 && <div className="w-12 h-px bg-gray-200 mt-10" />}
              </div>
            ))}
          </article>
          <div className="text-center mt-16 p-10 rounded-2xl" style={{ background: `${C.accent}08`, border: `1px solid ${C.accent}20` }}>
            <h3 className="text-2xl font-bold text-gray-900 mb-3" style={serif}>Ready to Book?</h3>
            <p className="text-gray-500 mb-6">Get in touch today for a free, no-obligation quote</p>
            <Button size="lg" className="text-white font-semibold px-10 h-13 text-base shadow-lg" style={{ background: C.accent, boxShadow: `0 8px 30px ${C.accent}30` }} onClick={() => setIsModalOpen(true)}>
              <PhoneCall className="w-5 h-5 mr-2" />Enquire Now
            </Button>
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id="contact" className="scroll-mt-20 py-24 bg-[#f7f7f9]" data-testid="contact-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>Get In Touch</h2>
            <p className="text-gray-500">Ready to book? Contact us today</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: <PhoneCall className="w-6 h-6" />, title: 'Call Us', detail: d.business.phone, href: 'tel:0428909900' },
              { icon: <Mail className="w-6 h-6" />, title: 'Email', detail: d.business.email, href: `mailto:${d.business.email}` },
              { icon: <MapPin className="w-6 h-6" />, title: 'Location', detail: d.business.location, href: null },
            ].map((c, i) => (
              <a key={i} href={c.href || '#'} className="block group">
                <div className="text-center p-8 rounded-2xl bg-white border border-gray-100 hover:shadow-lg transition-all duration-200">
                  <div className="w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 transition-colors" style={{ background: `${C.accent}10`, color: C.accent }}>
                    {c.icon}
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">{c.title}</h3>
                  <p className="text-gray-500 text-sm break-all">{c.detail}</p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ — Accordion ── */}
      <section className="py-24 bg-white" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4" style={serif}>FAQ</h2>
            <p className="text-gray-500">Everything you need to know</p>
          </div>
          <div className="space-y-3">
            {d.faqs.map((faq, i) => (
              <div key={i} className="rounded-xl border border-gray-200 overflow-hidden transition-all" style={{ background: openFaq === i ? `${C.accent}05` : 'white' }}>
                <button className="w-full flex items-center justify-between p-5 text-left" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                  <h3 className="font-semibold text-gray-900 text-[15px] pr-4">{faq.question}</h3>
                  <ChevronDown className={`w-5 h-5 flex-shrink-0 transition-transform duration-300 ${openFaq === i ? 'rotate-180' : ''}`} style={{ color: C.accent }} />
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${openFaq === i ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                  <p className="px-5 pb-5 text-gray-500 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t pt-16 pb-8 text-white" style={{ background: C.bg, borderColor: `${C.accent}10` }} data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <span className="text-xl font-bold" style={{ ...serif, color: C.accent }}>TW</span>
                <span className="text-white font-semibold text-sm">{d.business.name}</span>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">Melbourne's most trusted entertainment agency. Over 10 years delivering unforgettable events.</p>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                {navLinks.slice(1).map(l => <li key={l.to}><Link to={l.to} className="hover:text-white transition-colors">{l.label}</Link></li>)}
                {['Services', 'Gallery', 'Reviews', 'Contact'].map(i => <li key={i}><a href={`#${i.toLowerCase()}`} className="transition-colors" style={{ '--hover': C.accent }} onMouseEnter={e => e.target.style.color = C.accent} onMouseLeave={e => e.target.style.color = ''}>{i}</a></li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                {['Topless Waitressing', 'Strip Shows', 'Poker Dealers', 'Bucks Packages'].map(s => <li key={s}>{s}</li>)}
              </ul>
            </div>
            <div>
              <h4 className="text-[10px] uppercase tracking-[0.2em] text-gray-400 font-semibold mb-4">Contact</h4>
              <ul className="space-y-2 text-sm text-gray-500">
                <li>{d.business.phone}</li><li>{d.business.email}</li><li>{d.business.location}</li>
              </ul>
            </div>
          </div>
          <div className="border-t pt-8 text-center text-xs text-gray-600" style={{ borderColor: 'rgba(255,255,255,0.05)' }}>
            &copy; {new Date().getFullYear()} {d.business.name}. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Floating CTA */}
      <div className={`fixed bottom-6 left-6 z-50 transition-all duration-500 ${showFloat ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0 pointer-events-none'}`}>
        <Button className="text-white font-semibold px-6 h-12 rounded-full shadow-xl text-sm" style={{ background: C.accent, boxShadow: `0 8px 30px ${C.accent}40` }} onClick={() => setIsModalOpen(true)} data-testid="floating-cta">
          <PhoneCall className="w-4 h-4 mr-2" />Book Now
        </Button>
      </div>

      {/* Floating WhatsApp Button */}
      <a
        href="https://wa.me/61428909900"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 p-4 rounded-full bg-[#25D366] text-white shadow-lg hover:scale-110 transition-transform"
        aria-label="Contact us on WhatsApp"
        data-testid="whatsapp-button"
      >
        <MessageCircle className="w-6 h-6" />
      </a>

      <QuoteModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
};

export default HomePage;
